import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Loader2 } from 'lucide-react'
import { useCreateConfigKV, useUpdateConfigKV } from '@/app/hooks/useConfigKV'
import { ConfigKV } from '@/app/services/configKvService'

const configKVSchema = z.object({
  key: z.string().min(1, 'Key is required').regex(/^[A-Za-z0-9_.-]+$/, 'Key can only contain letters, numbers, dots, hyphens, and underscores'),
  value: z.string().min(1, 'Value is required'),
  description: z.string().optional(),
  data_type: z.enum(['string', 'number', 'boolean', 'json']),
  is_encrypted: z.boolean().default(false),
  expires_at: z.string().optional().nullable(),
  metadata: z.string().optional(),
  cache_duration_seconds: z.number().optional().nullable()
})

type ConfigKVFormValues = z.infer<typeof configKVSchema>

interface ConfigKVDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  config: ConfigKV | null
  scope: 'organization' | 'site'
  organizationId?: number
  siteId?: number
  sites?: any[]
}

const ConfigKVDialog: React.FC<ConfigKVDialogProps> = ({
  open,
  onOpenChange,
  config,
  scope,
  organizationId,
  siteId,
  sites = []
}) => {
  const createMutation = useCreateConfigKV()
  const updateMutation = useUpdateConfigKV()
  const isEdit = !!config
  const [selectedScope, setSelectedScope] = useState<'organization' | 'site'>(scope)
  const [selectedSiteId, setSelectedSiteId] = useState<number | undefined>(siteId)

  const form = useForm<ConfigKVFormValues>({
    resolver: zodResolver(configKVSchema),
    defaultValues: {
      key: '',
      value: '',
      description: '',
      data_type: 'string',
      is_encrypted: false,
      expires_at: null,
      metadata: '',
      cache_duration_seconds: null
    }
  })

  useEffect(() => {
    if (config) {
      form.reset({
        key: config.key,
        value: config.value,
        description: config.description || '',
        data_type: config.data_type,
        is_encrypted: config.is_encrypted,
        expires_at: config.expires_at,
        metadata: config.metadata ? JSON.stringify(config.metadata, null, 2) : '',
        cache_duration_seconds: config.cache_duration_seconds
      })
      setSelectedScope(config.scope as 'organization' | 'site')
      setSelectedSiteId(config.site_id || undefined)
    } else {
      form.reset({
        key: '',
        value: '',
        description: '',
        data_type: 'string',
        is_encrypted: false,
        expires_at: null,
        metadata: '',
        cache_duration_seconds: null
      })
      setSelectedScope(scope)
      setSelectedSiteId(siteId)
    }
  }, [config, form, scope, siteId])

  const onSubmit = async (values: ConfigKVFormValues) => {
    try {
      let metadata = undefined
      if (values.metadata) {
        try {
          metadata = JSON.parse(values.metadata)
        } catch {
          form.setError('metadata', { message: 'Invalid JSON format' })
          return
        }
      }

      const data = {
        ...values,
        metadata,
        scope: selectedScope,
        organization_id: selectedScope === 'organization' || selectedScope === 'site' ? organizationId : undefined,
        site_id: selectedScope === 'site' ? selectedSiteId : undefined
      }

      if (isEdit) {
        await updateMutation.mutateAsync({
          id: config.id,
          key: config.key,
          params: {
            scope: config.scope,
            organization_id: config.organization_id || undefined,
            site_id: config.site_id || undefined
          },
          data: {
            value: values.value,
            description: values.description,
            data_type: values.data_type,
            is_encrypted: values.is_encrypted,
            expires_at: values.expires_at,
            metadata,
            cache_duration_seconds: values.cache_duration_seconds
          }
        })
      } else {
        await createMutation.mutateAsync(data)
      }

      onOpenChange(false)
    } catch (error) {
      console.error('Failed to save config:', error)
    }
  }

  const validateValue = (value: string, dataType: string) => {
    switch (dataType) {
      case 'number':
        return !isNaN(Number(value))
      case 'boolean':
        return value === 'true' || value === 'false'
      case 'json':
        try {
          JSON.parse(value)
          return true
        } catch {
          return false
        }
      default:
        return true
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Configuration' : 'Add Configuration'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the configuration value and its settings.'
              : 'Create a new configuration key-value pair.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {!isEdit && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Scope</Label>
                  <Select
                    value={selectedScope}
                    onValueChange={(value: 'organization' | 'site') => setSelectedScope(value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select scope" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="organization">Organization</SelectItem>
                      <SelectItem value="site">Site</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {selectedScope === 'site' && (
                  <div className="space-y-2">
                    <Label>Site</Label>
                    <Select
                      value={selectedSiteId?.toString() || ''}
                      onValueChange={(value) => setSelectedSiteId(value ? Number(value) : undefined)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a site" />
                      </SelectTrigger>
                      <SelectContent>
                        {sites.map((site) => (
                          <SelectItem key={site.id} value={site.id.toString()}>
                            {site.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            <FormField
              control={form.control}
              name="key"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Key</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g., app.feature.enabled"
                      disabled={isEdit}
                    />
                  </FormControl>
                  <FormDescription>
                    Unique identifier for this configuration
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="data_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select data type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="string">String</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                        <SelectItem value="boolean">Boolean</SelectItem>
                        <SelectItem value="json">JSON</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cache_duration_seconds"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cache Duration (seconds)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        placeholder="e.g., 3600"
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Value</FormLabel>
                  <FormControl>
                    {form.watch('data_type') === 'boolean' ? (
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select true or false" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">true</SelectItem>
                          <SelectItem value="false">false</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : form.watch('data_type') === 'json' ? (
                      <Textarea
                        {...field}
                        placeholder='{"key": "value"}'
                        rows={4}
                        onChange={(e) => {
                          field.onChange(e)
                          const dataType = form.getValues('data_type')
                          if (!validateValue(e.target.value, dataType)) {
                            form.setError('value', {
                              message: `Invalid ${dataType} format`
                            })
                          } else {
                            form.clearErrors('value')
                          }
                        }}
                      />
                    ) : (
                      <Input
                        {...field}
                        placeholder={
                          form.watch('data_type') === 'number'
                            ? '123'
                            : 'Enter value'
                        }
                        onChange={(e) => {
                          field.onChange(e)
                          const dataType = form.getValues('data_type')
                          if (!validateValue(e.target.value, dataType)) {
                            form.setError('value', {
                              message: `Invalid ${dataType} format`
                            })
                          } else {
                            form.clearErrors('value')
                          }
                        }}
                      />
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Describe what this configuration does"
                      rows={2}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="is_encrypted"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>Encrypt Value</FormLabel>
                      <FormDescription className="text-xs">
                        Store this value encrypted
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expires_at"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expiration Date</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="datetime-local"
                        value={field.value || ''}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="metadata"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Metadata (JSON)</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder='{"category": "feature-flag", "version": "1.0"}'
                      rows={3}
                    />
                  </FormControl>
                  <FormDescription>
                    Additional metadata in JSON format
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {(createMutation.isPending || updateMutation.isPending) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isEdit ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default ConfigKVDialog